/**
 * WHETHER THIS BLOCK MAY TAKE A VARIABLE, and how to say so if it may not.
 *
 * Variables are the one capability with RUNGS that bite per block rather than per feature, so a
 * single boolean cannot carry the answer. Three states, and the difference between the last two
 * is the whole design:
 *
 *   absent    the capability is off. Nothing to learn yet, so no control — an inert button for a
 *             feature that does not exist is a false invitation.
 *   disabled  the capability is ON and this block is above the rung reached. The control STAYS,
 *             inert. The owner's reasoning, and it is the deciding one: "the richtext toolbar
 *             should be identical everywhere someone encounters it ... it's better that if
 *             variables are activated, but not allowable in a field, the button is disabled. that
 *             trains the user that it's a capability that can eventually be unlocked."
 *   allowed   insert away.
 *
 * It reconciles with `feedback_capability_vs_mode_gating` rather than breaking it. That rule says
 * a capability is ABSENT and a mode is ghosted-and-inert — and "variables on, but not in this
 * block" is not the capability being off. It is a boundary INSIDE an enabled capability, which is
 * the mode case: the feature is on, and this context does not take it.
 *
 * ## It never touches what variables EXIST
 *
 * `useTaskVariables` has nineteen consumers and feeds RESOLUTION as much as insertion — the roll,
 * the issue context, every chip's rendering. Emptying it to gate insertion would turn authored
 * chips into literal `{name}` text, which breaks both "off means hidden, never forbidden" and the
 * standing rule that a `{name}` is a chip or a value and never literal. So this is a second,
 * separate signal and the variable list is left alone.
 */
import { useAnswerRefScope } from './answer-ref-context';
import { useBlockIdentity } from './fired-states';

import { useCapabilities } from '@/hooks/use-capabilities';
import { CAPABILITIES } from '@/lib/capabilities';
import { variableAllowedInBlock } from '@/lib/variable-rungs';

export type VariableAffordance = 'absent' | 'disabled' | 'allowed';

export function useVariableAffordance(): VariableAffordance {
  const { level } = useCapabilities();
  const scope = useAnswerRefScope();
  const identity = useBlockIdentity();

  const rung = level('variables');
  if (rung <= 0) return 'absent';

  const self = identity?.blockId
    ? scope?.tasks.find(tk => tk.id === identity.taskId)?.blocks
      ?.find((b: { id: string }) => b.id === identity.blockId)
    : undefined;

  /*
   * An unknown block is ALLOWED, which is the same way the provider itself leans.
   *
   * A control mounted outside the providers — a story, a test harness, a surface that has not
   * been wrapped yet — is not a teacher at a gated block, and a wrong "disabled" there takes a
   * control away from someone who was entitled to it. A wrong "allowed" only offers one early.
   */
  if (!self?.type) return 'allowed';
  return variableAllowedInBlock(rung, self.type) ? 'allowed' : 'disabled';
}

/**
 * MAY THIS BLOCK ADOPT A VARIABLE-DRIVEN MECHANISM — the top rung, and the only thing on it
 * besides reaching every block.
 *
 * TWO mechanisms, not one, and missing the second is what made this hook's first name wrong:
 *
 *   branching corrects  the correct ANSWER varies with a control equation (8 blocks)
 *   as equations        each option or item's CONTENT is computed (MCQ, Dropdown, Ordering, Sort)
 *
 * Both are what the top rung of `variables` MEANS. Neither is a capability of its own, on the
 * evidence of the code rather than the idea: every one of them opens on `htmlHasVariableChips`
 * or an equation's variable names — a VARIABLE and nothing else. A recall chip opens none of them.
 *
 * ## Why the block-level gate does not already cover this
 *
 * It looks as though it should: none of these blocks can take a variable below the top rung, so
 * no chip should ever reach their options. PASTE is the hole, and the owner found it by doing
 * exactly that — a chip copied out of a Numerical block and pasted into an MCQ option is parsed
 * straight back into a chip by the extension's `parseHTML`, and the toggle it opens was ungated.
 * Lowering the rung after authoring at the top one leaves the same chips in place.
 *
 * So a mechanism gate cannot be inferred from the insert gate. It has to be asked for separately,
 * which is what this is.
 *
 * Against the registry's rung COUNT rather than a literal 3, so inserting a rung between sensors
 * and everywhere moves these with it instead of silently handing them to the new middle rung.
 *
 * `inUse` is the mechanism's own on/off state, where the caller has it: a block already using one
 * keeps the control, so the teacher can switch it off. Withholding it would leave a block
 * branching, or rendering computed options, with no way to stop — the Sort block had worked this
 * out for itself and already wrote `|| showBranching`. Callers that gate a VISIBILITY flag
 * instead pass nothing, because their own `|| itemsAsEquations` already says the same thing.
 */
export function useVariableMechanismAllowed(inUse = false): boolean {
  const { level } = useCapabilities();
  const top = CAPABILITIES.find(c => c.id === 'variables')?.rungs.length ?? 0;
  return inUse || (top > 0 && level('variables') >= top);
}
